import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TipoAtivo, TipoAtivoFormData } from '@/types/tipo-ativo';
import { useClientFilter } from '@/hooks/useClientFilter';

export const useTiposAtivos = () => {
  const { getClientFilter, clientLoading, isAdmin } = useClientFilter();
  const clientId = getClientFilter();

  return useQuery({
    queryKey: ['tipos-ativos', clientId],
    queryFn: async () => {
      let query = supabase
        .from('cmdb_tipos_ativos')
        .select('*')
        .order('nome');

      // Admins veem todos, outros usuários veem tipos do cliente + globais
      if (!isAdmin && clientId) {
        query = query.or(`client_id.eq.${clientId},client_id.is.null`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as TipoAtivo[];
    },
    enabled: !clientLoading,
  });
};

export const useTipoAtivo = (id?: string) => {
  const { getClientFilter } = useClientFilter();
  const clientId = getClientFilter();

  return useQuery({
    queryKey: ['tipo-ativo', id, clientId],
    queryFn: async () => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from('cmdb_tipos_ativos')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as TipoAtivo;
    },
    enabled: !!id,
  });
};

export const useCreateTipoAtivo = () => {
  const queryClient = useQueryClient();
  const { getClientFilter } = useClientFilter();

  return useMutation({
    mutationFn: async (data: TipoAtivoFormData) => {
      const clientId = getClientFilter();
      
      const { data: result, error } = await supabase
        .from('cmdb_tipos_ativos')
        .insert({
          ...data,
          client_id: data.client_id || clientId,
          campos_padrao: data.campos_padrao as any,
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipos-ativos'] });
      toast.success('Tipo de ativo criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar tipo de ativo: ' + error.message);
    },
  });
};

export const useUpdateTipoAtivo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TipoAtivoFormData> }) => {
      const { data: result, error } = await supabase
        .from('cmdb_tipos_ativos')
        .update({
          ...data,
          campos_padrao: data.campos_padrao as any,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipos-ativos'] });
      queryClient.invalidateQueries({ queryKey: ['tipo-ativo'] });
      toast.success('Tipo de ativo atualizado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao atualizar tipo de ativo: ' + error.message);
    },
  });
};

export const useDeleteTipoAtivo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cmdb_tipos_ativos')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipos-ativos'] });
      toast.success('Tipo de ativo deletado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao deletar tipo de ativo: ' + error.message);
    },
  });
};
